import {
  ColumnValues,
  FilterType,
  IGlobalFilters,
  isActiveFilter
} from '@DStypes/filter';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { useLazyGetColumnValuesQuery } from '@api/projects';
import {
  CustomSelect,
  FaIcon,
  If,
  MDBox,
  MDButton,
  MDInput,
  MDTypography
} from '@components';
import { setUserNotifications } from '@components/Notification/slice';
import { DATE_TIME_GROUP } from '@constants/common';
import { ADDED_DATE_FILTER } from '@constants/eventNames';
import { CHART_DATA_PROCESSING } from '@constants/messages';

import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  Switch,
  Tooltip,
  FormHelperText
} from '@mui/material';
import { RootState } from '@store/store';
import { sendAmplitudeData } from '@utils/amplitude';
import { getDateFormat } from '@utils/chart';
import dayjs from 'dayjs';
import deepEqual from 'deep-equal';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

const enum DateFilterTypes {
  LAST_FULL = 'last_full',
  THIS = 'this',
  SLIDER_FILTER = 'slider_filter'
}

interface FilterItemProps {
  isDashboardFilter?: boolean;
  columnId: string;
  projectId: string;
  columnName: string;
  filterType: FilterType;
  iconTooltipText?: string;
  isNewFilterItem?: boolean;
  columnsIncluded?: string[];
  handleFilterSubmit: (
    newFilter: IGlobalFilters,
    dynamic_filter: boolean
  ) => void;
  handleDeleteFilter: (
    key: string,
    isNewFilter: boolean,
    dynamic_filter: boolean
  ) => void;
  handleColumnToggleChange?: (
    value: boolean,
    columnName: string,
    dynamic_filter: boolean
  ) => void;
  activeStatus: isActiveFilter;
  sliderValues?: number[] | string[];
  isDate: 'DATE' | 'DATETIME' | '';
  index?: number;
  toggleDashboardFilter?: boolean;
  dynamicFilterType?: string;
  integerDateLimit?: number;
  dateGroupChoice?: string;
  baselineChoice?: string;
  dateGroup?: string;
}

const FilterItem: React.FC<FilterItemProps> = ({
  isDashboardFilter = false,
  columnId,
  columnName,
  filterType,
  iconTooltipText = '',
  projectId,
  isNewFilterItem = false,
  columnsIncluded = [],
  handleFilterSubmit,
  handleDeleteFilter,
  handleColumnToggleChange,
  activeStatus,
  sliderValues,
  isDate = '',
  index,
  toggleDashboardFilter = false,
  dynamicFilterType,
  integerDateLimit,
  dateGroupChoice,
  baselineChoice,
  dateGroup
}) => {
  const columnDataRefetchFunction = useRef(() => {});
  const dispatch = useDispatch();

  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );

  const [getColumnValues] = useLazyGetColumnValuesQuery();

  const [columnData, setColumnData] = useState<ColumnValues | null>(null);
  const [isColumnDataLoading, setColumnDataLoading] = useState(false);
  const [searchTextValue, setSearchTextValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [includedColumns, setIncludedColumns] =
    useState<string[]>(columnsIncluded);
  const [editFilter, setEditFilter] = useState(isNewFilterItem ? true : false);
  const [rangeSliderValues, setRangeSliderValues] = useState([0, 0]);
  const [error, setError] = useState('');

  const [changedSearchText, setChangedSearchText] = useState(false);

  const [dateFilterType, setDateFilterType] = useState(
    isNewFilterItem
      ? DateFilterTypes.LAST_FULL
      : dynamicFilterType || DateFilterTypes.SLIDER_FILTER
  );
  const [lastFullDateFilterValues, setLastFullDateFilterValues] = useState({
    integerDateLimit: integerDateLimit || '1',
    group_choice: dateGroupChoice || 'month',
    baselineChoice: baselineChoice || 'last_datapoint'
  });
  const [thisDateFilterValue, setThisDateFilterValue] = useState({
    group_choice: dateGroupChoice || 'month'
  });

  const isInitialLoad = useRef(true);

  useEffect(() => {
    const params = {
      project_id: projectId,
      field_id: columnId,
      ...(dateGroup && { date_group: dateGroup })
    };

    if (isColumnDataLoading || isInitialLoad) {
      // Function to refetch chart data if data is in processing state
      columnDataRefetchFunction.current = async () => {
        const response = await getColumnValues(params).unwrap();

        if (response.error === CHART_DATA_PROCESSING) {
          setTimeout(async () => columnDataRefetchFunction.current(), 800);
        } else {
          setColumnData(response);
          setColumnDataLoading(false);
        }
      };
      columnDataRefetchFunction.current();
      isInitialLoad.current = false;
    }
  }, [isColumnDataLoading]);

  useEffect(() => {
    if (isNewFilterItem && projectId && columnId) setColumnDataLoading(true);
  }, [isNewFilterItem]);

  useEffect(() => {
    if (
      isNewFilterItem &&
      (filterType === FilterType.RANGE || filterType === FilterType.DYNAMIC) &&
      columnData
    )
      if (isDate) setRangeSliderValues([0, columnData.values.length - 1]);
      else setRangeSliderValues(columnData?.values as number[]);

    if (
      !isNewFilterItem &&
      (filterType === FilterType.RANGE || filterType === FilterType.DYNAMIC) &&
      columnData
    )
      if (isDate) {
        const values: (string | number)[] = columnData.values;

        if (sliderValues) {
          const startIndex = values.indexOf(sliderValues[0]);
          const endIndex = values.indexOf(sliderValues[1]);

          setRangeSliderValues([startIndex, endIndex]);
        } else {
          setRangeSliderValues([0, columnData.values.length - 1]);
        }
      } else {
        if (sliderValues) setRangeSliderValues(sliderValues as number[]);
      }
  }, [columnData]);

  useEffect(() => {
    setDebouncedSearchText(searchTextValue);
  }, [searchTextValue]);

  const columnValues: string[] = useMemo(() => {
    if (columnData)
      if (!searchText) return columnData?.values?.map(String);
      else
        return columnData.values
          .map(String)
          .filter((column: string) =>
            column.toLowerCase().includes(searchText.toLowerCase())
          );
    else return [];
  }, [columnData, searchText]);

  useEffect(() => {
    if (isNewFilterItem && columnValues && !changedSearchText)
      setIncludedColumns(columnValues);
  }, [isNewFilterItem, columnValues]);

  const sendNotification = (msg_event: MSG_EVENT, message: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: true,
      is_user_message: true,
      message_event: msg_event,
      message_text: message,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.SUCCESS,
      message_data: JSON.stringify({
        chart_id: currentChartData?.id,
        project: {
          id: '',
          name: currentChartData?.chart_project_name
        }
      }),
      linkKeyword: 'here.',
      replaceKeyword: '<click here>',
      is_important: false,
      recipient_obj: 0,
      isAnchorElement: true
    };

    dispatch(setUserNotifications(payload));
  };

  const getDateString = (date: any) =>
    dayjs(date).format(getDateFormat(DATE_TIME_GROUP.DAYS));

  const sliderMarks = useMemo(() => {
    if (filterType !== FilterType.RANGE) return [];

    if (columnData) {
      const min = isDate ? 0 : (columnData.values[0] as number);
      const max = isDate
        ? columnData.values.length - 1
        : (columnData.values[1] as number);

      return [
        {
          value: min,
          label: isDate
            ? `${getDateString(columnData.values[min])}`
            : `${min.toLocaleString()}`
        },
        {
          value: max,
          label: isDate
            ? `${getDateString(columnData.values[max])}`
            : `${max.toLocaleString()}`
        }
      ];
    }
  }, [columnData, filterType]);

  const setDebouncedSearchText = useDebouncedCallback((text) => {
    setSearchText(text);
  }, 500);

  const handleXaxisSelection = (value: boolean, xAxis: string) => {
    if (value && !includedColumns.includes(xAxis))
      setIncludedColumns((prev) => [...prev, xAxis]);
    if (!value && includedColumns.includes(xAxis))
      setIncludedColumns((prev) =>
        prev.filter((item: string) => item !== xAxis)
      );
  };

  const handleSelectAll = (value: boolean) => {
    setSearchTextValue('');
    if (value) setIncludedColumns(columnValues);
    else setIncludedColumns([]);
  };

  const dateRangeLabel = useMemo(() => {
    if (isDate && columnData) {
      const startValue = getDateString(columnData.values[rangeSliderValues[0]]);
      const endValue = getDateString(columnData.values[rangeSliderValues[1]]);

      return `${startValue} - ${endValue}`;
    } else {
      return '';
    }
  }, [rangeSliderValues, columnData, isDate]);

  const handleOkButtonClick = () => {
    let modifiedFilter = {};

    let shouldUpdate = true;
    let dynamic_filter = false;

    if (filterType === FilterType.VALUES) {
      if (!isNewFilterItem && deepEqual(includedColumns, columnsIncluded))
        shouldUpdate = false;

      modifiedFilter = {
        [columnName]: {
          type: FilterType.VALUES,
          filterType: 'include',
          values: includedColumns,
          active: isActiveFilter.TRUE
        }
      };
    } else if (isDate && columnData) {
      if (dateFilterType === DateFilterTypes.SLIDER_FILTER) {
        const startValue = columnData.values[rangeSliderValues[0]];
        const endValue = columnData.values[rangeSliderValues[1]];

        if (
          !isNewFilterItem &&
          sliderValues &&
          sliderValues[0] === startValue &&
          sliderValues[1] === endValue
        )
          shouldUpdate = false;

        modifiedFilter = {
          [columnName]: {
            type: FilterType.RANGE,
            startValue: startValue,
            endValue: endValue,
            active: isActiveFilter.TRUE
          }
        };
        if (shouldUpdate)
          sendAmplitudeData(ADDED_DATE_FILTER, {
            filter_type: 'fixed'
          });
      } else if (dateFilterType === DateFilterTypes.LAST_FULL) {
        dynamic_filter = true;
        if (
          !Number.isInteger(
            Number(lastFullDateFilterValues?.integerDateLimit)
          ) ||
          !(Number(lastFullDateFilterValues?.integerDateLimit) >= 1)
        ) {
          setError('Only Numeric values allowed');

          return;
        } else {
          setError('');
        }
        modifiedFilter = {
          [columnName]: {
            filter: DateFilterTypes.LAST_FULL,
            number_of: Number(lastFullDateFilterValues?.integerDateLimit),
            group_choice: lastFullDateFilterValues?.group_choice,
            baseline: lastFullDateFilterValues?.baselineChoice,
            active: isActiveFilter.TRUE,
            field_id: columnId
          }
        };
        sendAmplitudeData(ADDED_DATE_FILTER, {
          filter_type: 'dynamic',
          last_full_or_this: DateFilterTypes.LAST_FULL,
          number_of: Number(lastFullDateFilterValues?.integerDateLimit),
          date_group_choice: lastFullDateFilterValues?.group_choice,
          baseline: lastFullDateFilterValues?.baselineChoice,
          field_id: columnId
        });
      } else {
        dynamic_filter = true;
        modifiedFilter = {
          [columnName]: {
            filter: DateFilterTypes.THIS,
            number_of: 1,
            group_choice: thisDateFilterValue.group_choice,
            baseline: 'current_time',
            active: isActiveFilter.TRUE,
            field_id: columnId
          }
        };
        sendAmplitudeData(ADDED_DATE_FILTER, {
          filter_type: 'dynamic',
          last_full_or_this: DateFilterTypes.THIS,
          number_of: 1,
          date_group_choice: thisDateFilterValue.group_choice,
          baseline: 'current_time'
        });
      }
    } else {
      if (
        !isNewFilterItem &&
        sliderValues &&
        sliderValues[0] === rangeSliderValues[0] &&
        sliderValues[1] === rangeSliderValues[1]
      )
        shouldUpdate = false;
      modifiedFilter = {
        [columnName]: {
          type: FilterType.RANGE,
          startValue: rangeSliderValues[0],
          endValue: rangeSliderValues[1],
          active: isActiveFilter.TRUE,
          field_id: columnId
        }
      };
    }
    if (!isNewFilterItem) setEditFilter(false);

    if (isNewFilterItem || shouldUpdate) {
      if (!isDashboardFilter && !toggleDashboardFilter)
        sendNotification(
          MSG_EVENT.CHART_FILTER_APPLIED,
          `You have successfully filtered the chart ${currentChartData.chart_title} by “${columnName}”`
        );
      handleFilterSubmit(modifiedFilter, dynamic_filter);
    }
  };

  const handleEditIconClick = () => {
    if (editFilter) {
      setError('');
      setEditFilter(false);
      setIncludedColumns(columnsIncluded);
      if (sliderValues && columnData)
        if (isDate) {
          const values: (string | number)[] = columnData.values;

          const startIndex = values.indexOf(sliderValues[0]);
          const endIndex = values.indexOf(sliderValues[1]);

          setRangeSliderValues([startIndex, endIndex]);
        } else {
          setRangeSliderValues(sliderValues as number[]);
        }
      setDateFilterType(dynamicFilterType || DateFilterTypes.SLIDER_FILTER);
      setLastFullDateFilterValues({
        integerDateLimit: integerDateLimit || '1',
        group_choice: dateGroupChoice || 'month',
        baselineChoice: baselineChoice || 'last_datapoint'
      });
      setThisDateFilterValue({
        group_choice: dateGroupChoice || 'month'
      });
    } else {
      setEditFilter(true);
      if (projectId && columnId) setColumnDataLoading(true);
    }
  };

  const getSliderStep = (minValue: number, maxValue: number) => {
    if (!Number.isInteger(maxValue) && Number.isFinite(maxValue)) {
      // Calculate the decimal place value based on the length of the decimal part
      const decimalPlaces = (maxValue.toString().split('.')[1] || '').length;

      return Math.pow(10, -decimalPlaces);
    } else {
      if (Number.isInteger(minValue)) {
        return 1;
      } else if (Number.isFinite(minValue)) {
        const decimalPlaces = (minValue.toString().split('.')[1] || '').length;

        return Math.pow(10, -decimalPlaces);
      } else {
        // Handle cases where input is not a number
        return 1;
      }
    }
  };

  const onDateFilterTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError('');
    setDateFilterType(event.target.value as DateFilterTypes);
  };

  const dateGroupChoices = [
    {
      value: 'hour',
      label: 'hour'
    },
    {
      value: 'day',
      label: 'day'
    },
    {
      value: 'week',
      label: 'week'
    },
    {
      value: 'month',
      label: 'month'
    },
    {
      value: 'quarter',
      label: 'quarter'
    },
    {
      value: 'year',
      label: 'year'
    },
    {
      value: 'decade',
      label: 'decade'
    }
  ];

  const baselineOptions = [
    {
      value: 'current_time',
      label: 'today'
    },
    {
      value: 'last_datapoint',
      label: 'last datapoint'
    }
  ];

  return (
    <Grid>
      <MDBox
        display='flex'
        gap='4px'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
        sx={{ marginBottom: 1.5 }}
      >
        <MDBox display='flex' gap='4px' alignItems='flex-end'>
          <Tooltip
            title={
              isDashboardFilter || activeStatus === isActiveFilter.TRUE
                ? iconTooltipText
                : ''
            }
          >
            <MDBox sx={{ display: 'flex' }}>
              {!isNewFilterItem &&
                !isDashboardFilter &&
                handleColumnToggleChange && (
                  <Switch
                    sx={{
                      mt: '.1rem',
                      width: '2.3rem',
                      height: '1rem',
                      '& .MuiSwitch-thumb': {
                        width: '.75rem',
                        height: '.75rem'
                      }
                    }}
                    checked={
                      activeStatus === isActiveFilter.TRUE ? true : false
                    }
                    onChange={(e, value) => {
                      handleColumnToggleChange(
                        value,
                        columnName,
                        dynamicFilterType ? true : false
                      );
                    }}
                  />
                )}

              <IconButton sx={{ padding: 0 }}>
                <FaIcon
                  typographyProps={{
                    variant: 'sm',
                    color: 'info',
                    marginRight: '5px',
                    marginLeft: '5px'
                  }}
                  icon={
                    isDashboardFilter ? ['far', 'objects-column'] : 'signal'
                  }
                />
              </IconButton>
            </MDBox>
          </Tooltip>

          <Tooltip
            title={
              columnName.length > (isDashboardFilter ? 20 : 10)
                ? `${columnName} ${isDashboardFilter ? '' : ' (Chart)'}`
                : ''
            }
            placement='top'
          >
            <MDTypography color='info' fontSize='0.875rem' fontWeight='400'>
              {index ? `${index}. ` : ''}
              {`${
                columnName.slice(0, isDashboardFilter ? 20 : 10) +
                (columnName.length > (isDashboardFilter ? 20 : 10) ? '...' : '')
              } ${isDashboardFilter ? '' : ' (Chart)'}`}
            </MDTypography>
          </Tooltip>
        </MDBox>
        <MDBox display='flex' gap='6px' alignItems='center'>
          {isColumnDataLoading && (
            <CircularProgress
              color='secondary'
              size='20px'
              sx={{ marginRight: '2px' }}
            />
          )}
          {!isNewFilterItem &&
            activeStatus !== isActiveFilter.FALSE &&
            !editFilter && (
              <FaIcon
                typographyProps={{
                  variant: 'sm',
                  color: 'info',
                  marginRight: '5px'
                }}
                style={{
                  cursor: 'pointer'
                }}
                icon='pencil'
                onClick={handleEditIconClick}
              />
            )}
          {editFilter && (
            <MDButton
              color='primary'
              variant='gradient'
              size='small'
              sx={{ display: 'flex', justifySelf: 'flex-end' }}
              onClick={handleOkButtonClick}
              disabled={isColumnDataLoading}
            >
              OK
            </MDButton>
          )}
          <FaIcon
            typographyProps={{
              variant: 'sm',
              color: 'info',
              marginRight: '5px'
            }}
            style={{
              cursor: 'pointer'
            }}
            icon={editFilter ? 'xmark' : 'trash'}
            onClick={() =>
              editFilter && !isNewFilterItem
                ? handleEditIconClick()
                : handleDeleteFilter(
                    columnName,
                    isNewFilterItem,
                    dynamicFilterType ? true : false
                  )
            }
          />
        </MDBox>
      </MDBox>
      <If condition={!isColumnDataLoading}>
        {/* For numbers*/}
        {filterType === FilterType.RANGE &&
          editFilter &&
          rangeSliderValues?.length > 0 &&
          columnData &&
          !isDate && (
            <Grid container alignItems={'center'} px={1} mb={2}>
              <Tooltip title={''} placement='top'>
                <Grid item flex={1} mt={1}>
                  <MDBox sx={{ textAlign: 'center' }}>
                    <MDTypography
                      id='slider'
                      gutterBottom
                      variant='xs'
                      color='info'
                    >
                      {`${rangeSliderValues[0].toLocaleString()} - ${rangeSliderValues[1].toLocaleString()}`}
                    </MDTypography>
                  </MDBox>
                  <MDBox display='flex' justifyContent='center' mb={1}>
                    <Slider
                      value={rangeSliderValues as number[]}
                      onChange={(e, values) => {
                        setRangeSliderValues(values as number[]);
                      }}
                      sx={{
                        '	.MuiSlider-mark': {
                          display: 'none'
                        },
                        width: '190px'
                      }}
                      min={columnData?.values[0] as number}
                      max={columnData?.values[1] as number}
                      marks={sliderMarks}
                      step={getSliderStep(
                        columnData?.values[0] as number,
                        columnData?.values[1] as number
                      )}
                    />
                  </MDBox>
                  <Divider variant={'fullWidth'} sx={{ opacity: 1, flex: 1 }} />
                </Grid>
              </Tooltip>
            </Grid>
          )}
        {/* Date filters */}
        {(filterType === FilterType.RANGE ||
          filterType === FilterType.DYNAMIC) &&
          editFilter &&
          rangeSliderValues?.length > 0 &&
          columnData &&
          isDate && (
            <Grid container alignItems={'center'} px={1} mb={2}>
              <RadioGroup onChange={onDateFilterTypeChange}>
                <FormControlLabel
                  control={
                    <Tooltip
                      title='Choose to select an entire hour, week, month. Example: First to Last day of x number of months.'
                      placement='top'
                    >
                      <Radio
                        color='secondary'
                        size='medium'
                        sx={{
                          mb: 0.3
                        }}
                      />
                    </Tooltip>
                  }
                  sx={{ mb: 2 }}
                  value={DateFilterTypes.LAST_FULL}
                  label={
                    <MDBox
                      display='flex'
                      flexDirection='column'
                      alignItems='flex-start'
                      gap={2}
                    >
                      <MDBox
                        display='flex'
                        flexDirection='row'
                        alignContent='center'
                        justifyContent='center'
                        alignItems='center'
                        gap={2}
                      >
                        <MDTypography variant='body'>Last full</MDTypography>
                        <MDInput
                          value={lastFullDateFilterValues?.integerDateLimit}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setLastFullDateFilterValues((prev) => ({
                              ...prev,
                              integerDateLimit: e.target.value
                            }))
                          }
                          sx={{
                            width: '50px',
                            '& input': {
                              height: '8px'
                            }
                          }}
                        />
                        <CustomSelect
                          options={dateGroupChoices}
                          value={lastFullDateFilterValues?.group_choice}
                          handleValueChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setLastFullDateFilterValues((prev) => ({
                              ...prev,
                              group_choice: e.target.value
                            }))
                          }
                        />
                      </MDBox>
                      <MDBox
                        display='flex'
                        flexDirection='row'
                        alignContent='center'
                        justifyContent='center'
                        alignItems='center'
                        gap={2}
                      >
                        <MDTypography variant='body' lineHeight='1.25rem'>
                          until
                        </MDTypography>
                        <CustomSelect
                          options={baselineOptions}
                          value={lastFullDateFilterValues?.baselineChoice}
                          handleValueChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setLastFullDateFilterValues((prev) => ({
                              ...prev,
                              baselineChoice: e.target.value
                            }))
                          }
                        />
                      </MDBox>
                      {dateFilterType === DateFilterTypes.LAST_FULL &&
                        lastFullDateFilterValues.group_choice === 'week' && (
                          <MDBox
                            display='flex'
                            flexDirection='row'
                            gap={1}
                            alignItems='flex-end'
                          >
                            <Checkbox checked={true} />
                            <MDTypography variant='h6' color='info'>
                              Week starts from Monday
                            </MDTypography>
                          </MDBox>
                        )}
                    </MDBox>
                  }
                  checked={dateFilterType === DateFilterTypes.LAST_FULL}
                />

                <FormControlLabel
                  control={
                    <Tooltip
                      title="Filter from the beginning of the current day, week, month, etc; until today's date"
                      placement='top'
                    >
                      <Radio
                        color='secondary'
                        size='medium'
                        sx={{
                          mb: 0.3
                        }}
                      />
                    </Tooltip>
                  }
                  checked={dateFilterType === DateFilterTypes.THIS}
                  value={DateFilterTypes.THIS}
                  label={
                    <MDBox display='flex' flexDirection='column' gap={1.5}>
                      <MDBox
                        display='flex'
                        flexDirection='row'
                        alignContent='center'
                        justifyContent='center'
                        alignItems='center'
                        gap={2}
                      >
                        <MDTypography variant='body'>This</MDTypography>
                        <CustomSelect
                          options={dateGroupChoices}
                          value={thisDateFilterValue?.group_choice}
                          handleValueChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setThisDateFilterValue((prev) => ({
                              ...prev,
                              group_choice: e.target.value
                            }))
                          }
                        />
                      </MDBox>
                      {dateFilterType === DateFilterTypes.THIS &&
                        thisDateFilterValue.group_choice === 'week' && (
                          <MDBox
                            display='flex'
                            flexDirection='row'
                            gap={1}
                            alignItems='flex-end'
                          >
                            <Checkbox checked={true} />
                            <MDTypography variant='h6' color='info'>
                              Week starts from Monday
                            </MDTypography>
                          </MDBox>
                        )}
                    </MDBox>
                  }
                />
                <FormControlLabel
                  control={
                    <Tooltip
                      title='Use this to customise your date selection for filter. Example: 16th Jan to 16th Feb.'
                      placement='top'
                    >
                      <Radio
                        color='secondary'
                        size='medium'
                        sx={{
                          mb: 0.3
                        }}
                      />
                    </Tooltip>
                  }
                  checked={dateFilterType === DateFilterTypes.SLIDER_FILTER}
                  value={DateFilterTypes.SLIDER_FILTER}
                  label={
                    <MDBox
                      display='flex'
                      flexDirection='row'
                      alignContent='center'
                      justifyContent='center'
                      alignItems='flex-end'
                      gap={2}
                    >
                      <MDTypography variant='body'>
                        Choose a period
                      </MDTypography>
                    </MDBox>
                  }
                />
              </RadioGroup>
              {error && (
                <FormHelperText
                  sx={{ mb: 1, ml: '25px', display: 'block', mt: 0.5 }}
                  error
                >
                  {error}
                </FormHelperText>
              )}
              <Tooltip title={''} placement='top'>
                <Grid item flex={1} mt={1}>
                  {dateFilterType === DateFilterTypes.SLIDER_FILTER && (
                    <>
                      <MDBox sx={{ textAlign: 'center' }}>
                        <MDTypography
                          id='slider'
                          gutterBottom
                          variant='xs'
                          color='info'
                        >
                          {`${dateRangeLabel}`}
                        </MDTypography>
                      </MDBox>

                      <MDBox display='flex' justifyContent='center' mb={1}>
                        <Slider
                          value={rangeSliderValues as number[]}
                          onChange={(e, values) => {
                            setRangeSliderValues(values as number[]);
                          }}
                          sx={{
                            '	.MuiSlider-mark': {
                              display: 'none'
                            },
                            width: '190px'
                          }}
                          min={0}
                          max={columnData.values.length - 1}
                          marks={sliderMarks}
                          step={1}
                        />
                      </MDBox>
                    </>
                  )}
                  <Divider variant={'fullWidth'} sx={{ opacity: 1, flex: 1 }} />
                </Grid>
              </Tooltip>
            </Grid>
          )}
        {/*Text values*/}
        {filterType === FilterType.VALUES && editFilter && (
          <MDBox mb={2}>
            <Grid container alignItems='center'>
              <Grid item xs={1} marginLeft='8px'>
                <Tooltip
                  title='Select to select all. Un-select to clear all.'
                  placement='top'
                >
                  <Checkbox
                    checked={columnValues?.every((value: string) =>
                      includedColumns?.includes(value)
                    )}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleSelectAll(event.target.checked)
                    }
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={10}>
                <MDInput
                  fullWidth
                  label='Search'
                  value={searchTextValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchTextValue(e.target.value);
                    setChangedSearchText(true);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <FaIcon
                          icon='search'
                          typographyProps={{ variant: 'sm', color: 'dark' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
            {columnValues?.length > 0 && (
              <MDBox sx={{ marginLeft: 1 }}>
                <MDBox
                  sx={{
                    maxHeight: !isDashboardFilter ? '160px' : '300px',
                    overflow: 'scroll',
                    my: 2
                  }}
                >
                  {columnValues.map((item: any, index: number) => (
                    <FormControlLabel
                      key={index}
                      checked={includedColumns?.includes(item)}
                      onChange={(e, value) => {
                        handleXaxisSelection(value, item);
                      }}
                      control={<Checkbox sx={{ marginLeft: '11px' }} />}
                      label={
                        <Tooltip
                          title={item.length > 30 ? item : ''}
                          placement='top'
                        >
                          <MDTypography variant='h6' color='info'>
                            {item.slice(0, 30) +
                              `${item.length > 30 ? '...' : ''}`}
                          </MDTypography>
                        </Tooltip>
                      }
                    />
                  ))}
                </MDBox>
                <MDBox px={1} mb={2}>
                  <Divider variant={'fullWidth'} sx={{ opacity: 1, flex: 1 }} />
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        )}
      </If>
    </Grid>
  );
};

export default FilterItem;
